import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Brain, Check, Clock, Search, PenLine, Zap, Send, Mic, Paperclip } from 'lucide-react';
import { RISK_BRIEFINGS } from '../../data/riskBriefingData';
import { scaleDelay } from '../../hooks/demoSpeed';

const AGENT_THUMBS = {
  stark: '/Thumbnails/Stark.png',
  marsi: '/Thumbnails/Marsi.png',
  sally: '/Thumbnails/Sally.png',
  wright: '/Thumbnails/Wright.png',
  zira: '/Thumbnails/Ziira.png',
  buzz: '/Thumbnails/Buzz.png',
  dora: '/Thumbnails/Dora.png',
  tali: '/Thumbnails/Tali.png',
  finn: '/Thumbnails/Finn.png',
  luc: '/Thumbnails/Luc.png',
};
const AGENT_NAMES = { stark: 'Stark', marsi: 'Marsi', sally: 'Sally', wright: 'Wright', zira: 'Zira', buzz: 'Buzz', dora: 'Dora', tali: 'Tali', finn: 'Finn', luc: 'Luc' };

const V4RiskBriefing = ({ isDark, riskId, onBack, onExecute }) => {
  const data = RISK_BRIEFINGS[riskId];
  const primaryAgent = data?.agent || 'stark';
  const primaryAgentName = AGENT_NAMES[primaryAgent] || 'Stark';
  const [thinkingStep, setThinkingStep] = useState(-1);
  const [thinkingDone, setThinkingDone] = useState(false);
  const [briefingIndex, setBriefingIndex] = useState(-1);
  const [planVisible, setPlanVisible] = useState(false);
  const [outroVisible, setOutroVisible] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatSent, setChatSent] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const timerRefs = useRef([]);
  const scrollRef = useRef(null);

  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const agentText = `text-[14px] leading-[1.8] ${isDark ? 'text-white/85' : 'text-slate-700'}`;

  const addTimer = (cb, delay) => { const id = setTimeout(cb, scaleDelay(delay)); timerRefs.current.push(id); };

  useEffect(() => {
    timerRefs.current.forEach(clearTimeout);
    timerRefs.current = [];
    setThinkingStep(-1); setThinkingDone(false); setBriefingIndex(-1); setPlanVisible(false); setOutroVisible(false);
    setChatInput(''); setChatSent(false); setChatMessages([]);

    if (!data) return;
    const steps = data.thinkingSteps || [];
    let delay = 800;
    steps.forEach((_, i) => { addTimer(() => setThinkingStep(i), delay); delay += 2200; });
    // Thinking done badge
    addTimer(() => { setThinkingStep(-1); setThinkingDone(true); }, delay);
    delay += 1000;
    // Briefing paragraphs one by one
    data.briefing.forEach((_, i) => {
      addTimer(() => setBriefingIndex(i), delay);
      delay += 1800;
    });
    // Plan
    addTimer(() => setPlanVisible(true), delay);
    delay += 2000;
    // Outro + CTA
    addTimer(() => setOutroVisible(true), delay);

    return () => timerRefs.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thinkingStep, thinkingDone, briefingIndex, planVisible, outroVisible, chatMessages]);

  const handleChatSend = () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    // Stark responds after a brief delay
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        role: 'agent',
        text: "Good call. I've adjusted the plan to account for that. The core approach stays the same — Marsi on targeting, Wright on content, Sally on re-scoring — but I've tweaked the priorities based on your input. Ready to execute whenever you are."
      }]);
    }, 2000);
    setChatSent(true);
  };

  if (!data) return null;

  const thinkingSteps = data.thinkingSteps || [];
  const currentStep = thinkingStep >= 0 && thinkingStep < thinkingSteps.length ? thinkingSteps[thinkingStep] : null;

  return (
    <div className="flex flex-col h-full" data-testid="risk-briefing-view">
      <style>{`@keyframes riskBriefFade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      {/* Header */}
      <div className={`flex items-center gap-3 px-5 py-3.5 border-b ${divider} flex-shrink-0`}>
        <button onClick={onBack} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'hover:bg-white/[0.06] text-white/60 hover:text-white' : 'hover:bg-black/[0.04] text-slate-500 hover:text-slate-800'}`} data-testid="risk-briefing-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Briefing
        </button>
        <div className={`h-4 w-px ${divider}`} />
        <img src={AGENT_THUMBS[primaryAgent]} alt="" className="w-6 h-6 rounded-full object-cover" />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${textPrimary}`}>Resolving: {data.title}</p>
          <p className={`text-[10px] ${textSecondary}`}>{primaryAgentName} — Risk Analysis & Resolution</p>
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-[80%] mx-auto px-4 py-8">

        {/* Priya's opener (optional user message above agent response) */}
        {data.openerText && (
          <div className="flex justify-end mb-8">
            <div className={`max-w-[85%] px-5 py-3.5 rounded-2xl rounded-br-md text-[14px] leading-[1.8] ${isDark ? 'bg-indigo-500/20 text-white' : 'bg-indigo-100 border border-indigo-200/80 text-slate-800'}`}>{data.openerText}</div>
          </div>
        )}

        {/* Thinking steps */}
        {currentStep && (
          <div className={`rounded-xl px-5 py-4 ${isDark ? 'bg-indigo-500/8 border border-indigo-500/10' : 'bg-indigo-50 border border-indigo-200/40'}`} style={{ animation: 'riskBriefFade 0.3s ease-out' }}>
            <div className="space-y-3.5">
              {thinkingSteps.map((step, i) => {
                const isActive = i === thinkingStep;
                const isDone = i < thinkingStep;
                const isPending = i > thinkingStep;
                const StepIcon = i === 0 ? Brain : i === 1 ? Search : PenLine;
                return (
                  <div key={i} className={`flex items-center gap-3 transition-opacity duration-300 ${isPending ? 'opacity-20' : ''}`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? (isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-500/15 text-emerald-600') : isActive ? (isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-500/15 text-indigo-600') : (isDark ? 'bg-white/[0.06] text-white/20' : 'bg-black/[0.04] text-slate-400')}`}>
                      {isDone ? <Check className="w-3 h-3" /> : isActive ? <StepIcon className="w-3 h-3 animate-pulse" /> : <span className="w-1 h-1 rounded-full bg-current" />}
                    </div>
                    <div>
                      <span className={`text-[12px] font-medium ${isActive ? (isDark ? 'text-indigo-400' : 'text-indigo-600') : isDone ? (isDark ? 'text-white/60' : 'text-slate-600') : ''}`}>{step.label}</span>
                      {(isActive || isDone) && <span className={`text-[11px] ml-2 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>{step.detail}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Thinking time */}
        {thinkingDone && !currentStep && (
          <div className="flex items-center gap-2" style={{ marginTop: 16, animation: 'riskBriefFade 0.3s ease-out' }}>
            <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-white/30' : 'text-slate-400'}`} />
            <span className={`text-[11px] ${isDark ? 'text-white/30' : 'text-slate-400'}`}>Thought for {data.thinkingTime}s</span>
          </div>
        )}

        {/* Briefing paragraphs — one by one */}
        {briefingIndex >= 0 && (
          <div className="flex items-start gap-4" style={{ marginTop: 48, animation: 'riskBriefFade 0.4s ease-out' }}>
            <img src={AGENT_THUMBS[primaryAgent]} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
            <div className={agentText}>
              {data.briefing.slice(0, briefingIndex + 1).map((p, i, arr) => (
                <p key={i} style={{ marginBottom: i < arr.length - 1 ? 20 : 0, animation: i === arr.length - 1 ? 'riskBriefFade 0.4s ease-out' : 'none' }}>{p}</p>
              ))}
            </div>
          </div>
        )}

        {/* Resolution plan */}
        {planVisible && (
          <div style={{ marginTop: 48, animation: 'riskBriefFade 0.4s ease-out' }}>
            <div className="flex items-start gap-4">
              <img src={AGENT_THUMBS[primaryAgent]} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
              <div className={agentText}>
                <p style={{ marginBottom: 24 }}>{data.plan.intro}</p>
              </div>
            </div>

            {/* Agent plan cards */}
            <div className="ml-[52px]" style={{ marginTop: 16 }}>
              {data.plan.agents.map((a, i) => (
                <div key={i} className={`flex items-start gap-3 px-5 py-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-black/[0.01] border-black/[0.05]'}`} style={{ marginTop: i === 0 ? 0 : 24, animation: `riskBriefFade 0.35s ease-out ${i * 0.1}s both` }}>
                  <img src={AGENT_THUMBS[a.agent]} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-0.5" />
                  <div>
                    <p className={`text-[13px] font-semibold ${textPrimary}`}>{AGENT_NAMES[a.agent]} <span className={`font-normal ${textSecondary}`}>— {a.role}</span></p>
                    <ul className="mt-2 space-y-1">
                      {a.tasks.map((t, j) => (
                        <li key={j} className={`text-[13px] leading-[1.6] ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                          <span className={textSecondary}>•</span> {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Outro + Execute CTA */}
        {outroVisible && (
          <div style={{ marginTop: 48, animation: 'riskBriefFade 0.4s ease-out' }}>
            <div className="flex items-start gap-4">
              <img src={AGENT_THUMBS[primaryAgent]} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
              <div className={agentText}>{data.plan.outro}</div>
            </div>
            <div className="flex items-center gap-3 ml-[52px]" style={{ marginTop: 24 }}>
              <button
                onClick={onExecute}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13px] font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:opacity-90 transition-opacity"
                data-testid="risk-execute-btn"
              >
                <Zap className="w-4 h-4" /> Execute Plan
              </button>
              <button
                onClick={onBack}
                className={`px-5 py-2.5 rounded-xl text-[13px] font-medium border ${divider} ${textSecondary} ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'} transition-colors`}
                data-testid="risk-edit-plan-btn"
              >
                Edit Plan
              </button>
            </div>
          </div>
        )}

        {/* Chat messages from editing */}
        {chatMessages.map((msg, i) => (
          <div key={i} style={{ marginTop: 48, animation: 'riskBriefFade 0.35s ease-out' }}>
            {msg.role === 'user' ? (
              <div className="flex justify-end">
                <div className={`max-w-[70%] px-5 py-3.5 rounded-2xl rounded-br-md text-[14px] leading-[1.8] ${isDark ? 'bg-indigo-500/20 text-white' : 'bg-indigo-100 border border-indigo-200/80 text-slate-800'}`}>{msg.text}</div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <img src={AGENT_THUMBS[primaryAgent]} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
                <div className={agentText}>{msg.text}</div>
              </div>
            )}
          </div>
        ))}

        <div ref={scrollRef} />
        </div>
      </div>

      {/* Chat input — always visible; disabled while streaming */}
      <div className="flex justify-center pb-4 px-4 pt-2 flex-shrink-0">
        <div className="w-[80%]">
          <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${divider} rounded-2xl flex flex-col ${!outroVisible ? 'opacity-60' : ''}`}>
            <div className="px-4 pt-3 pb-2">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                placeholder={outroVisible ? `Message ${primaryAgentName}...` : `${primaryAgentName} is working through this...`}
                rows={1}
                disabled={!outroVisible}
                className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${textPrimary} placeholder:${textSecondary}`}
                style={{ height: 'auto', maxHeight: '200px' }}
                data-testid="risk-briefing-chat-input"
              />
            </div>
            <div className="flex items-center justify-between px-3 pb-3">
              <div className="flex items-center gap-1">
                <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.08] ring-1 ring-indigo-500/40' : 'bg-indigo-50 ring-1 ring-indigo-300/60'}`}>
                  <img src={AGENT_THUMBS[primaryAgent]} alt="" className="w-5 h-5 rounded-full object-cover" />
                  <span className={`text-xs font-medium ${textPrimary}`}>{primaryAgentName}</span>
                </div>
                <button className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'}`}>
                  <Paperclip className={`w-3.5 h-3.5 ${textSecondary}`} />
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'}`}>
                  <Mic className={`w-4 h-4 ${textSecondary}`} />
                </button>
                <button
                  onClick={handleChatSend}
                  disabled={!outroVisible || !chatInput.trim()}
                  className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${outroVisible && chatInput.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`}
                  data-testid="risk-briefing-chat-send"
                >
                  <Send className={`w-3.5 h-3.5 ${outroVisible && chatInput.trim() ? 'text-white' : textSecondary}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default V4RiskBriefing;
